import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RCTable, { Column } from 'rc-table';
import { Button } from 'react-bootstrap';
import { format } from 'date-fns';

import { randomInteger } from 'utils';
import { postCellRequest } from 'modules/manager/containers/Booking/services/actions';
import { Reservations, ReservCell } from 'entities/Reservations';
import { reservationsSelector } from 'modules/manager/containers/Booking/services/selectors';
import { setModalBook, setModalEditBook } from 'modules/common/BookingModal/actions';
import BookingModal from 'modules/common/BookingModal';
import BookingHelper from './utils';
import { useModal } from '../hooks';
import { RCTableType } from '../DataGrid/interfaces';
import { usePrepareGrid } from './hooks';
import Table from './components/Table';
import HeaderCell from './components/HeaderCell';
import BookingComp from './components/BookingComp';
import YesNoModal from '../YesNoModal';
import ModalWrapper from '../ModalWrapper';
import styles from './BookingTable.module.scss';
import BookSlotModal from '../BookSlotModal';
import { RootState } from '../../../rootReducer';
import { bookingModalSelector } from '../../common/BookingModal/selector';

type Props = {
  times: any;
  viewStartDate: Date;
  schema: any;
  mode: 'director' | 'manager' | 'ordering';
  duration: number;
  onBook: any;
  cellLocked: any;
  maxReservationCount: number;
};

type Columns = {
  title: string;
  dataIndex: string;
  key: string;
  width: number;
};

const withGridBookingTable = (TableComponent: RCTableType) =>
  React.memo((props: Props) => {
    const { times, viewStartDate, schema, mode, onBook, duration, cellLocked, maxReservationCount } = props;
    const dispatch = useDispatch();

    const { books } = useSelector(reservationsSelector);

    const isManager = mode === 'manager';
    const isDirector = mode === 'director';
    const isOrdering = mode === 'ordering';
    const [dates, setDates] = useState<Columns[]>([]);
    const [cellDate, setCellDate] = useState<string>('');
    const [rowCell, setRowCell] = useState<any>([]);

    const { bookCell } = useSelector(bookingModalSelector);

    const auth = useSelector((state: RootState) => state.authModuleReducer.authReducer);

    const { isShow: isShowOK, openModal: openModalOK, closeModal: closeModalOK } = useModal();
    const { isShow: isShowBlock, openModal: openModalBlock, closeModal: closeModalBlock } = useModal();
    const { isShow: isShowUnBlock, openModal: openModalUnBlock, closeModal: closeModalUnBlock } = useModal();
    const {
      isShow: isNotBookableModal,
      openModal: openNotBookableModal,
      closeModal: closeNotBookableModal,
    } = useModal();

    const gridData = usePrepareGrid(books, dates, times, cellLocked, isManager, auth?.account?.username);

    const workingDays = useSelector(
      (state: RootState) => state.managerModuleReducer.reservationsReducer.cellSetting,
    )?.workingDays;

    const fillupDates = () => {
      const day = new Date(viewStartDate);
      const week = [
        {
          title: '',
          dataIndex: 'time',
          key: 'time',
          width: 100,
        },
      ];

      week.push({
        title: BookingHelper.formatDate(day),
        dataIndex: BookingHelper.formatDate(day),
        key: BookingHelper.formatDate(day),
        width: 100,
      });

      // eslint-disable-next-line
      for (let aa = 0; aa < 6; aa++) {
        const newDay = new Date(day.setDate(day.getDate() + 1));
        week.push({
          title: BookingHelper.formatDate(newDay),
          dataIndex: BookingHelper.formatDate(newDay),
          key: BookingHelper.formatDate(newDay),
          width: 100,
        });
      }
      return setDates(week);
    };

    // открывается модалка бука если кликнули по существующему буку
    const handleOpenBook = (b: Reservations) => {
      if (books) {
        const cell = books.find((c: any) => c.id === b.id);

        if (cell) {
          if (isDirector) {
            closeModalOK();
            setRowCell([]);
          }
          dispatch(setModalBook(cell));
          dispatch(setModalEditBook(b));
        }
      }
    };

    const onBlockCell = () => {
      const splitDate = cellDate.split('.');
      const date = new Date(`${splitDate[2]}, ${splitDate[1]}, ${splitDate[0]}, ${rowCell.time}`);
      const checkOutTime = new Date(date).getMinutes() + +duration;
      const checkOutDate = new Date(date).setMinutes(checkOutTime);
      const checkInDateF = format(date, "yyyy-MM-dd'T'HH:mm:ss");
      const checkOutDateF = format(new Date(checkOutDate), "yyyy-MM-dd'T'HH:mm:ss");
      dispatch(postCellRequest(checkInDateF, checkOutDateF, false, 0));
      closeModalUnBlock();
    };

    // eslint-disable-next-line
    const onRowDoubleClick = (rowInfo: any) => {
      return {
        onDoubleClick: () => {
          const dayOfWeek = new Date().getDay();
          if (
            !(
              (dayOfWeek === 1 && workingDays.isMondayWorking) ||
              (dayOfWeek === 2 && workingDays.isTuesdayWorking) ||
              (dayOfWeek === 3 && workingDays.isWednesdayWorking) ||
              (dayOfWeek === 4 && workingDays.isThursdayWorking) ||
              (dayOfWeek === 5 && workingDays.isFridayWorking) ||
              (dayOfWeek === 6 && workingDays.isSaturdayWorking) ||
              (dayOfWeek === 7 && workingDays.isSundayWorking)
            )
          ) {
            openNotBookableModal();
            return;
          }

          // @ts-ignore
          const splitDate = cellDate.split('.');
          const date = Date.parse(
            new Date(`${splitDate[2]}, ${splitDate[1]}, ${splitDate[0]}, ${rowInfo.time}`).toString(),
          );
          if (
            !cellDate ||
            format(new Date(`${splitDate[2]}, ${splitDate[1]}, ${splitDate[0]}`), 'dd.MM.yyyy') ===
              format(new Date(), 'dd.MM.yyyy') ||
            Date.now() > date
          ) {
            return;
          }

          if (
            rowInfo[cellDate] &&
            (rowInfo[cellDate][0]?.isTomorrowNotBookable || rowInfo[cellDate][0]?.isWeekendNotBookable)
          ) {
            return;
          }

          if (isDirector && cellDate) {
            if (rowInfo[cellDate]?.length || rowInfo[cellDate][0]?.id !== '00000000-0000-0000-0000-000000000000') {
              const exist = rowInfo[cellDate][0]?.reservations?.find(
                (r: Reservations) => r.bookingStatus === 1 || r.bookingStatus === 2,
              );

              if (exist) {
                return openModalOK();
              }

              if (rowInfo[cellDate][0]?.isBlocked) {
                setRowCell(rowInfo);
                return openModalUnBlock();
              }
            }
            setRowCell(rowInfo);
            if (!isShowUnBlock) {
              return openModalBlock();
            }
          }

          if (isManager && cellDate) {
            if (rowInfo[cellDate]?.length >= maxReservationCount && !bookCell) {
              openModalOK();
              return;
            }

            if (rowInfo[cellDate] && rowInfo[cellDate][0]?.isBlocked) {
              return;
            }

            if (!rowInfo[cellDate] || !rowInfo[cellDate][0]) {
              const checkOutTime = new Date(date).getMinutes() + +duration;
              const checkOutDatef = new Date(date).setMinutes(checkOutTime);
              const checkInDate = format(date, "yyyy-MM-dd'T'HH:mm:ss");
              const bookingDate = format(date, "yyyy-MM-dd'T'HH:mm:ss");
              const checkOutDate = format(new Date(checkOutDatef), "yyyy-MM-dd'T'HH:mm:ss");

              // @ts-ignore
              const cellReserve: ReservCell = { checkInDate, checkOutDate, bookingDate };
              return dispatch(setModalBook(cellReserve));
            }

            dispatch(setModalBook(rowInfo[cellDate][0]));
          }
          if (isOrdering && cellDate) {
            // eslint-disable-next-line no-useless-return
            return;
          }
        },
      };
    };

    useEffect(() => {
      fillupDates();
    }, [books]);

    return (
      <div className={styles.rcalendar}>
        <TableComponent
          data={gridData}
          onRow={(e: any) => onRowDoubleClick(e)}
          key={randomInteger(0, 999)}
          rowKey={`${randomInteger(0, 999)}`}
          components={{
            table: Table,
            header: {
              cell: HeaderCell,
            },
          }}
        >
          {dates.map((date: Columns) => (
            <Column
              title={date.title}
              dataIndex={date.dataIndex}
              className="position-relative"
              key={date.key}
              render={(value: any) => (
                <BookingComp
                  book={value}
                  handleOpen={handleOpenBook}
                  isManager={isManager}
                  onCreateBook={() => setCellDate(date.title)}
                  setCellDate={setCellDate}
                  maxReservationCount={maxReservationCount}
                />
              )}
            />
          ))}
        </TableComponent>

        <BookingModal schema={schema} onBook={onBook} mode={mode} />

        <BookSlotModal
          isShow={isShowBlock}
          closeModal={() => closeModalBlock()}
          rowCell={rowCell}
          cellDate={cellDate}
          duration={duration}
        />

        <YesNoModal
          isShow={isShowUnBlock}
          title="Дозволити броні для даного часу (розблокувати)"
          acceptHandler={onBlockCell}
          closeModal={() => {
            setRowCell([]);
            closeModalUnBlock();
          }}
        />

        <ModalWrapper
          show={isNotBookableModal}
          onHide={() => {
            closeNotBookableModal();
          }}
        >
          <div>
            <h6 className="text-center">Бронювання дозволено тільки у рабочі дні складу.</h6>
            <br />
            <br />
            <div className="d-flex justify-content-center mt-3">
              <Button
                className="px-5"
                variant="outline-secondary"
                onClick={() => {
                  closeNotBookableModal();
                }}
              >
                Ok
              </Button>
            </div>
          </div>
        </ModalWrapper>

        <ModalWrapper
          show={isShowOK}
          onHide={() => {
            setRowCell([]);
            closeModalOK();
          }}
        >
          <div>
            <h6 className="text-center">Ємність даного слоту перевищено.</h6>
            <h6 className="text-center">Оберіть інший слот.</h6>
            <div className="d-flex justify-content-center mt-3">
              <Button
                className="px-5"
                variant="outline-secondary"
                onClick={() => {
                  setRowCell([]);
                  closeModalOK();
                }}
              >
                Ok
              </Button>
            </div>
          </div>
        </ModalWrapper>
      </div>
    );
  });

export const BookingTable = withGridBookingTable(RCTable);
